import { useQuery } from '@tanstack/react-query';
import axios from 'src/utils/axios';

interface ConfigItem {
  key: string;
  value: unknown;
  visibility?: 'public' | 'private';
}

interface AdminConfigItem {
  key: string;
  value: Record<string, unknown>;
  visibility: 'public' | 'private';
  description: string;
  created_at: string;
  updated_at: string;
  updated_by?: string;
}

interface UIConfigOptions {
  prefix?: string;
  keys?: string[];
  usePublicEndpoint?: boolean; // New option to use public endpoint
}

// Helper function to determine if a config key should use public endpoint
function shouldUsePublicEndpoint(key?: string, prefix?: string): boolean {
  if (key) {
    return key.startsWith('ui.') || key.startsWith('feature.') || key.startsWith('public.');
  }
  if (prefix) {
    return (
      prefix.startsWith('ui.') || prefix.startsWith('feature.') || prefix.startsWith('public.')
    );
  }
  return false;
}

// Generic hook to fetch UI configurations
export function useUIConfigs(options?: UIConfigOptions) {
  const usePublic =
    options?.usePublicEndpoint ?? shouldUsePublicEndpoint(options?.keys?.[0], options?.prefix);

  return useQuery({
    queryKey: ['uiConfigs', options, usePublic],
    queryFn: async () => {
      const params: Record<string, string> = {};

      // Add prefix filter if specified
      if (options?.prefix) {
        params.prefix = options.prefix;
      }

      // Choose endpoint based on whether we need public configs
      const endpoint = usePublic ? '/v1/config' : '/v1/admin/config';
      const response = await axios.get<{ items: ConfigItem[] }>(endpoint, { params });

      // If specific keys were requested, filter the results
      if (options?.keys && options.keys.length > 0) {
        return response.items.filter((item) => options.keys!.includes(item.key));
      }

      return response.items;
    },
    // Cache for 5 minutes
    staleTime: 5 * 60 * 1000,
    // Keep in cache for 10 minutes
    gcTime: 10 * 60 * 1000,
  });
}

// Specific hook for a single UI config value
export function useUIConfig<T = unknown>(key: string, defaultValue: T) {
  const { data: configs, ...queryResult } = useUIConfigs({ keys: [key] });

  const configItem = configs?.find((item) => item.key === key);
  const value = configItem?.value ?? defaultValue;

  return {
    value: value as T,
    ...queryResult,
  };
}

// Hook specifically for the highlighter config
export function useHighlighterConfig() {
  const { value, ...queryResult } = useUIConfig('ui.highlighter.showAll', { enabled: true });

  // Extract the enabled value from the config object
  const enabled =
    typeof value === 'object' && value !== null && 'enabled' in value
      ? (value as { enabled: boolean }).enabled
      : true;

  return {
    showAllHighlights: enabled,
    ...queryResult,
  };
}

// Hook specifically for public configurations
export function usePublicConfigs() {
  return useQuery({
    queryKey: ['adminConfigs', 'public'],
    queryFn: async () => {
      const response = await axios.get<{ items: AdminConfigItem[] }>('/v1/admin/config', {
        params: {
          visibility: 'public',
          size: 50,
        },
      });
      return response.items;
    },
    // Cache for 5 minutes
    staleTime: 5 * 60 * 1000,
    // Keep in cache for 10 minutes
    gcTime: 10 * 60 * 1000,
  });
}

// Hook specifically for private configurations
export function usePrivateConfigs() {
  return useQuery({
    queryKey: ['adminConfigs', 'private'],
    queryFn: async () => {
      const response = await axios.get<{ items: AdminConfigItem[] }>('/v1/admin/config', {
        params: {
          visibility: 'private',
          exclude: ['user.rateLimit.'],
          size: 50,
        },
      });
      return response.items;
    },
    // Cache for 5 minutes
    staleTime: 5 * 60 * 1000,
    // Keep in cache for 10 minutes
    gcTime: 10 * 60 * 1000,
  });
}

// Hook to determine if a claim should be highlighted
export function useClaimHighlight() {
  const { showAllHighlights } = useHighlighterConfig();

  return {
    shouldHighlight: (assessment: string) => {
      if (showAllHighlights) {
        // Show all highlights: true (green), false (red), no consensus (yellow)
        return true;
      } else {
        // Show only false claims (red)
        return assessment === 'FALSE';
      }
    },
    getHighlightColor: (assessment: string) => {
      if (assessment === 'TRUE') return 'green';
      if (assessment === 'FALSE') return 'red';
      return 'grey'; // No consensus
    },
  };
}

// Hook specifically for the pricing feature flag
export function usePricingFeature() {
  const { value, ...queryResult } = useUIConfig('feature.pricing.enabled', { enabled: false });

  // Extract the enabled value from the config object
  const enabled =
    typeof value === 'object' && value !== null && 'enabled' in value
      ? (value as { enabled: boolean }).enabled
      : false;

  return {
    isPricingEnabled: enabled,
    ...queryResult,
  };
}
